import React, { useState, useRef, useEffect, useCallback, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  FileText, Archive, Eye, ZoomIn, ZoomOut, Copy, Check, ExternalLink, 
  ChevronLeft, ChevronRight, Maximize2, X, Move
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

// ==================== PAGE PARSER UTILITY ====================

export const parsePageReference = (pageRef) => {
  if (!pageRef) return { pageStart: null, pageEnd: null };
  
  // Handle various formats: "17", "p17", "p. 17", "page 17", "17-19", "pp. 17-19", "pages 17-19"
  const cleanRef = pageRef.toLowerCase().replace(/\s+/g, ' ').trim();
  
  // Try range first: "17-19", "p. 17-19", "pages 17-19"
  const rangeMatch = cleanRef.match(/(?:p(?:p|ages?)?\.?\s*)?(\d+)\s*[-–—]\s*(\d+)/i);
  if (rangeMatch) {
    return { 
      pageStart: parseInt(rangeMatch[1]), 
      pageEnd: parseInt(rangeMatch[2]) 
    };
  }
  
  // Single page: "17", "p17", "p. 17", "page 17"
  const singleMatch = cleanRef.match(/(?:p(?:age)?\.?\s*)?(\d+)/i);
  if (singleMatch) {
    return { 
      pageStart: parseInt(singleMatch[1]), 
      pageEnd: null 
    };
  }
  
  return { pageStart: null, pageEnd: null };
};

// ==================== MEDIA VIEWER CONTEXT ====================

const MediaViewerContext = createContext();

export const MediaViewerProvider = ({ children }) => {
  const [viewerState, setViewerState] = useState({
    isOpen: false,
    mediaAsset: null,
    initialPage: 1,
    initialZoom: 1,
  });

  const openViewer = useCallback((mediaAsset, options = {}) => {
    setViewerState({
      isOpen: true,
      mediaAsset,
      initialPage: options.initialPage || 1,
      initialZoom: options.initialZoom || 1,
    });
  }, []);

  const closeViewer = useCallback(() => {
    setViewerState(prev => ({ ...prev, isOpen: false }));
  }, []);

  return (
    <MediaViewerContext.Provider value={{ viewerState, openViewer, closeViewer }}>
      {children}
      <GlobalMediaViewer />
    </MediaViewerContext.Provider>
  );
};

export const useMediaViewer = () => {
  const context = useContext(MediaViewerContext);
  if (!context) {
    return { openViewer: () => {}, closeViewer: () => {} };
  }
  return context;
};

// ==================== GLOBAL MEDIA VIEWER MODAL ====================

const GlobalMediaViewer = () => {
  const { viewerState, closeViewer } = useContext(MediaViewerContext) || {};
  const { isOpen, mediaAsset, initialPage, initialZoom } = viewerState || {};
  
  const [zoom, setZoom] = useState(1);
  const [pdfPage, setPdfPage] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  
  // Reset state when asset changes
  useEffect(() => {
    if (isOpen && mediaAsset) {
      setZoom(initialZoom || 1);
      setPdfPage(initialPage || 1);
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen, mediaAsset, initialPage, initialZoom]);

  // Handle keyboard
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') closeViewer?.();
      if (e.key === '+' || e.key === '=') setZoom(z => Math.min(z + 0.25, 4));
      if (e.key === '-') setZoom(z => Math.max(z - 0.25, 0.25));
      if (e.key === '0') { setZoom(1); setPosition({ x: 0, y: 0 }); }
      if (mediaAsset?.type === 'pdf') {
        if (e.key === 'ArrowRight') setPdfPage(p => p + 1);
        if (e.key === 'ArrowLeft') setPdfPage(p => Math.max(1, p - 1));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, mediaAsset, closeViewer]);

  // Mouse drag for panning
  const handleMouseDown = (e) => {
    if (zoom > 1 && mediaAsset?.type !== 'pdf') {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  if (!isOpen || !mediaAsset) return null;

  const isPdf = mediaAsset.type === 'pdf';
  const fileUrl = mediaAsset.filename ? `${API}/media/file/${mediaAsset.filename}` : mediaAsset.source;

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/90 flex flex-col"
      onClick={(e) => e.target === e.currentTarget && closeViewer?.()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/50 text-white">
        <div className="flex-1">
          <h3 className="font-medium truncate">{mediaAsset.caption || mediaAsset.filename || "Media Viewer"}</h3>
          {mediaAsset.source && (
            <p className="text-xs text-gray-400 truncate">Source: {mediaAsset.source}</p>
          )}
        </div>
        
        <div className="flex items-center gap-2 mx-4">
          {isPdf ? (
            <>
              <button 
                onClick={() => setPdfPage(p => Math.max(1, p - 1))}
                className="p-2 hover:bg-white/20 rounded"
                data-testid="pdf-prev-page"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={pdfPage}
                  onChange={(e) => setPdfPage(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-16 h-8 text-center bg-white/10 border-white/20 text-white"
                  min={1}
                />
                <span className="text-sm">Page</span>
              </div>
              <button 
                onClick={() => setPdfPage(p => p + 1)}
                className="p-2 hover:bg-white/20 rounded"
                data-testid="pdf-next-page"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={() => setZoom(z => Math.max(z - 0.25, 0.25))}
                className="p-2 hover:bg-white/20 rounded"
                disabled={zoom <= 0.25}
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              <span className="text-sm min-w-[4rem] text-center">{Math.round(zoom * 100)}%</span>
              <button 
                onClick={() => setZoom(z => Math.min(z + 0.25, 4))}
                className="p-2 hover:bg-white/20 rounded"
                disabled={zoom >= 4}
              >
                <ZoomIn className="w-5 h-5" />
              </button>
              <button 
                onClick={() => { setZoom(1); setPosition({ x: 0, y: 0 }); }}
                className="p-2 hover:bg-white/20 rounded text-xs"
              >
                Reset
              </button>
              {zoom > 1 && (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Move className="w-3 h-3" /> Drag to pan
                </span>
              )}
            </>
          )}
        </div>
        
        <button 
          onClick={closeViewer}
          className="p-2 hover:bg-white/20 rounded"
          data-testid="close-media-viewer"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      {/* Content */}
      <div 
        ref={containerRef}
        className="flex-1 overflow-hidden flex items-center justify-center"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ cursor: zoom > 1 && !isPdf ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
      >
        {isPdf ? (
          <iframe 
            src={`${fileUrl}#page=${pdfPage}`}
            className="w-full h-full bg-white"
            title={mediaAsset.caption || "PDF Document"}
            key={pdfPage}
          />
        ) : (
          <img 
            src={fileUrl}
            alt={mediaAsset.alt_text || mediaAsset.caption || "Archive image"}
            style={{ 
              transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
              transition: isDragging ? 'none' : 'transform 0.2s',
              maxWidth: '90vw',
              maxHeight: '85vh',
              objectFit: 'contain'
            }}
            onError={(e) => { e.target.src = "https://placehold.co/800x600/1a1a1a/666?text=Image+Not+Found"; }}
            draggable={false}
          />
        )}
      </div>
      
      {/* Page range indicator for PDF */}
      {isPdf && viewerState?.pageEnd && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-amber-600 text-white px-3 py-1 rounded text-sm">
          Evidence spans pages {viewerState.initialPage}–{viewerState.pageEnd}
        </div>
      )}
    </div>
  );
};

// Claim Status Badge
export const ClaimStatusBadge = ({ status }) => {
  const statusClass = {
    "Documented": "status-documented",
    "Disputed": "status-disputed",
    "Unverified": "status-unverified"
  }[status] || "status-unverified";
  
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded ${statusClass}`} data-testid={`claim-status-${status?.toLowerCase()}`}>
      {status}
    </span>
  );
};

// Verification Badge
export const VerificationBadge = ({ status }) => {
  const styles = {
    "Verified": "bg-green-50 text-green-700 border-green-200",
    "Partially Verified": "bg-amber-50 text-amber-700 border-amber-200",
    "Unverified": "bg-slate-100 text-slate-600 border-slate-200",
    "Disputed": "bg-orange-50 text-orange-700 border-orange-200"
  };
  
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded border ${styles[status] || styles["Unverified"]}`} data-testid={`verification-${status?.toLowerCase().replace(' ', '-')}`}>
      {status}
    </span>
  );
};

// Confidence Badge
export const ConfidenceBadge = ({ level }) => {
  const styles = {
    "High": "confidence-high",
    "Medium": "confidence-medium",
    "Low": "confidence-low"
  };
  
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded ${styles[level] || "confidence-low"}`}>
      {level} Confidence
    </span>
  );
};

// Citation List Component with Deep-Linking
export const CitationList = ({ citations, onOpenEvidence }) => {
  const [expanded, setExpanded] = useState({});
  const { openViewer } = useMediaViewer();
  
  if (!citations || citations.length === 0) {
    return <p className="text-sm text-gray-500 italic">No citations available</p>;
  }
  
  const handleOpenEvidence = async (citation) => {
    const { pageStart, pageEnd } = parsePageReference(citation.page_or_timecode);
    
    // If we have an archive_file_id, fetch and open in viewer
    if (citation.archive_file_id) {
      try {
        const res = await api.get(`/media/${citation.archive_file_id}`);
        const mediaAsset = res.data;
        openViewer(mediaAsset, { 
          initialPage: pageStart || 1,
          pageEnd: pageEnd 
        });
        return;
      } catch (err) {
        console.error("Failed to load media asset", err);
      }
    }
    
    // If URL points to a file we can display
    if (citation.url) {
      const isInternalMedia = citation.url.includes('/api/media/file/');
      const isPdf = citation.url.toLowerCase().endsWith('.pdf');
      const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(citation.url);
      
      if (isInternalMedia || isPdf || isImage) {
        const filename = citation.url.split('/').pop();
        const type = isPdf ? 'pdf' : 'image';
        openViewer({
          type,
          filename: isInternalMedia ? filename : null,
          source: citation.url,
          caption: citation.title,
        }, { 
          initialPage: pageStart || 1,
          pageEnd: pageEnd
        });
        return;
      }
      
      // External link - open in new tab
      window.open(citation.url, '_blank');
    }
  };
  
  return (
    <div className="space-y-3">
      {citations.map((citation) => {
        const { pageStart, pageEnd } = parsePageReference(citation.page_or_timecode);
        const hasEvidence = citation.archive_file_id || citation.url;
        
        return (
          <div key={citation.id} className="citation-block">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="font-medium text-sm">{citation.title}</p>
                <p className="citation-source">
                  {citation.publisher && `${citation.publisher}, `}
                  {citation.date_published}
                  {citation.page_or_timecode && ` — ${citation.page_or_timecode}`}
                </p>
              </div>
              <Badge variant="outline" className="text-xs ml-2">
                {citation.source_type}
              </Badge>
            </div>
            
            <div className="flex flex-wrap gap-2 mt-2">
              {citation.excerpt && (
                <button
                  onClick={() => setExpanded(prev => ({ ...prev, [citation.id]: !prev[citation.id] }))}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  {expanded[citation.id] ? "Hide excerpt" : "Show excerpt"}
                </button>
              )}
              
              {hasEvidence && (
                <button
                  onClick={() => handleOpenEvidence(citation)}
                  className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 font-medium"
                  data-testid="open-evidence-btn"
                >
                  <Eye className="w-3 h-3" />
                  Open Evidence
                  {pageStart && (
                    <span className="text-gray-500">
                      {pageEnd ? `(pp. ${pageStart}-${pageEnd})` : `(p. ${pageStart})`}
                    </span>
                  )}
                </button>
              )}
            </div>
            
            {expanded[citation.id] && citation.excerpt && (
              <p className="mt-2 text-sm text-gray-600 italic border-l-2 border-gray-200 pl-3">
                &ldquo;{citation.excerpt}&rdquo;
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Evidence Drawer Component
export const EvidenceDrawer = ({ claimIds, title }) => {
  const [claims, setClaims] = useState([]);
  const [citations, setCitations] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const loadEvidence = async () => {
    if (!claimIds || claimIds.length === 0) return;
    setLoading(true);
    try {
      const claimPromises = claimIds.map(id => api.get(`/claims/${id}`));
      const results = await Promise.all(claimPromises);
      const claimsData = results.map(r => r.data.claim);
      const citationsData = results.flatMap(r => r.data.citations);
      setClaims(claimsData);
      setCitations(citationsData);
    } catch (err) {
      console.error("Failed to load evidence", err);
    }
    setLoading(false);
  };
  
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button 
          onClick={loadEvidence}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800"
          data-testid="evidence-drawer-trigger"
        >
          <FileText className="w-4 h-4" />
          Evidence
        </button>
      </SheetTrigger>
      <SheetContent className="evidence-drawer w-[450px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="serif-heading">{title || "Evidence & Citations"}</SheetTitle>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-100px)] mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {claims.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
                    Related Claims ({claims.length})
                  </h4>
                  {claims.map((claim) => (
                    <div key={claim.id} className="mb-4 p-3 bg-white rounded border">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="text-sm font-medium">{claim.claim_text}</p>
                        <ClaimStatusBadge status={claim.claim_status} />
                      </div>
                      {claim.summary && (
                        <p className="text-xs text-gray-600">{claim.summary}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {citations.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
                    Citations ({citations.length})
                  </h4>
                  <CitationList citations={citations} />
                </div>
              )}
              
              {claims.length === 0 && citations.length === 0 && (
                <p className="text-gray-500 text-center py-8">No evidence linked to this item</p>
              )}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

// Revision Meta Component
export const RevisionMeta = ({ updatedAt, itemType, itemId }) => {
  const revisionLink = itemType && itemId 
    ? `/corrections-log?item_type=${itemType}&item_id=${itemId}`
    : "/corrections-log";
  
  return (
    <div className="flex items-center gap-4 text-xs text-gray-500 border-t pt-3 mt-4">
      <span>Last updated: {new Date(updatedAt).toLocaleDateString()}</span>
      <Link to={revisionLink} className="text-blue-600 hover:text-blue-800">
        Revision history
      </Link>
    </div>
  );
};

// CiteThis Component
export const CiteThis = ({ title, publisher, date, pageRef, url, siteTitle = "Elvis Aloha 1973 Historical Archive" }) => {
  const [copied, setCopied] = useState(null);
  const accessedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const year = date ? new Date(date).getFullYear() : "n.d.";
  const formattedDate = date ? new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : "n.d.";
  
  const citations = {
    chicago: `"${title}." ${publisher || siteTitle}${date ? `, ${formattedDate}` : ""}${pageRef ? `, ${pageRef}` : ""}. Accessed ${accessedDate}.${url ? ` ${url}` : ""}`,
    mla: `"${title}." ${publisher || siteTitle}${date ? `, ${formattedDate}` : ""}${pageRef ? `, ${pageRef}` : ""}. Web. ${accessedDate}.`,
    apa: `${title}. (${year}). ${publisher || siteTitle}.${pageRef ? ` ${pageRef}.` : ""}${url ? ` Retrieved from ${url}` : ""}`
  };
  
  const copyToClipboard = async (style) => {
    try {
      await navigator.clipboard.writeText(citations[style]);
      setCopied(style);
      toast.success("Citation copied!");
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };
  
  return (
    <div className="border-t pt-4 mt-6" data-testid="cite-this-component">
      <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-2">
        <Copy className="w-4 h-4" />
        Cite This
      </h4>
      <Tabs defaultValue="chicago" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-3">
          <TabsTrigger value="chicago" className="text-xs">Chicago</TabsTrigger>
          <TabsTrigger value="mla" className="text-xs">MLA</TabsTrigger>
          <TabsTrigger value="apa" className="text-xs">APA</TabsTrigger>
        </TabsList>
        {Object.entries(citations).map(([style, text]) => (
          <TabsContent key={style} value={style}>
            <div className="bg-gray-50 p-3 rounded border text-sm font-mono leading-relaxed relative overflow-hidden">
              <p className="pr-8 break-all">{text}</p>
              <button
                onClick={() => copyToClipboard(style)}
                className="absolute top-2 right-2 p-1.5 hover:bg-gray-200 rounded transition-colors"
                data-testid={`copy-${style}-citation`}
              >
                {copied === style ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-500" />
                )}
              </button>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

// Media Thumbnail Component - Opens Global Viewer
export const MediaThumbnail = ({ mediaAsset, initialPage, className = "" }) => {
  const { openViewer } = useMediaViewer();
  
  if (!mediaAsset) return null;
  
  const isPdf = mediaAsset.type === 'pdf';
  const imageUrl = mediaAsset.filename ? `${API}/media/file/${mediaAsset.filename}` : mediaAsset.source;
  
  const handleClick = () => {
    openViewer(mediaAsset, { initialPage: initialPage || 1 });
  };
  
  if (isPdf) {
    return (
      <button 
        onClick={handleClick}
        className={`flex items-center gap-2 p-3 border rounded hover:bg-gray-50 transition-colors w-full text-left ${className}`}
        data-testid="media-thumbnail-pdf"
      >
        <FileText className="w-5 h-5 text-red-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium block truncate">{mediaAsset.caption || mediaAsset.filename || "PDF Document"}</span>
          {mediaAsset.source && <span className="text-xs text-gray-500 truncate block">{mediaAsset.source}</span>}
        </div>
        {initialPage && <Badge variant="outline" className="text-xs">Page {initialPage}</Badge>}
      </button>
    );
  }
  
  return (
    <button 
      onClick={handleClick}
      className={`cursor-pointer group block ${className}`}
      data-testid="media-thumbnail-image"
    >
      <div className="relative overflow-hidden rounded border bg-gray-100">
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={mediaAsset.alt_text || mediaAsset.caption || "Archive image"}
            className="w-full h-48 object-cover transition-transform group-hover:scale-105"
            onError={(e) => { e.target.src = "https://placehold.co/400x300/f5f3ef/666?text=Archive+Image"; }}
          />
        ) : (
          <div className="w-full h-48 flex items-center justify-center">
            <Archive className="w-12 h-12 text-gray-300" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <Maximize2 className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
      {mediaAsset.caption && <p className="text-xs text-gray-500 mt-1 text-left">{mediaAsset.caption}</p>}
    </button>
  );
};

// Legacy EnhancedMediaViewer - Now uses global viewer
export const EnhancedMediaViewer = ({ mediaId, filename, type, alt, caption, source, pageNumber }) => {
  const mediaAsset = {
    id: mediaId,
    type: type || 'image',
    filename,
    alt_text: alt,
    caption,
    source
  };
  
  return <MediaThumbnail mediaAsset={mediaAsset} initialPage={pageNumber} />;
};

// Re-export scroll management components
export { default as ScrollToTop } from "./ScrollToTop";
export { default as ScrollManager } from "./ScrollManager";
export { default as ScrollToTopButton } from "./ScrollToTopButton";

// Re-export media components
export { PrimaryDocuments } from "./PrimaryDocuments";

// Re-export brand components
export { default as BrandLogo } from "./BrandLogo";
